import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, Check, Loader2, Crown, Zap, Building } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { subscriptionAPI } from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

export const SelectPlan = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [starting, setStarting] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    // If not authenticated, redirect to login
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Check if user already has an active subscription
    const checkSubscription = async () => {
      try {
        const response = await subscriptionAPI.getStatus();
        const status = response.data;
        
        // If user has a valid subscription, redirect to dashboard
        const hasValidSubscription = 
          status.plan && 
          status.plan !== 'none' && 
          ['active', 'trialing'].includes(status.status);
        
        if (hasValidSubscription) {
          console.log('User has active subscription, redirecting to dashboard');
          navigate('/dashboard', { replace: true });
          return;
        }
        
        setCheckingSubscription(false);
        fetchTiers();
      } catch (error) {
        console.error('Failed to check subscription:', error);
        // If we can't check, still allow them to see the page
        setCheckingSubscription(false);
        fetchTiers();
      }
    };
    
    if (isAuthenticated) {
      checkSubscription();
    }
  }, [authLoading, isAuthenticated, navigate]);

  const fetchTiers = async () => {
    try {
      const response = await subscriptionAPI.getTiers();
      setTiers(response.data.tiers || {});
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }

    setStarting(true);
    try {
      // Redirect to Stripe checkout for payment setup with trial
      const originUrl = window.location.origin;
      const response = await subscriptionAPI.upgrade(selectedPlan, billingCycle, originUrl);
      
      if (response.data.url) {
        // Clear the subscription selection flag before redirecting
        localStorage.removeItem('needs_subscription_selection');
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Failed to start checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setStarting(false);
    }
  };

  const planFeatures = {
    professional: [
      'Case Management',
      'Client Portal',
      'Document Management',
      'Calendar & Deadlines',
      'Time Tracking',
      'Basic Invoicing',
      'Trust Accounting',
      'E-Signatures',
      'Email Integration'
    ],
    expert: [
      'Everything in Professional',
      'AI Legal Assistant',
      'Advanced Reports',
      'Workflow Automations',
      'Lead Management',
      'Expense Tracking',
      'Team Chat',
      'Case Intelligence',
      'Priority Support'
    ]
  };

  if (loading || authLoading || checkingSubscription) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8"
      data-testid="select-plan-page"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Scale className="h-10 w-10 text-blue-500" />
            <span className="text-3xl font-bold text-white font-['Plus_Jakarta_Sans']">LEAP Pro</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Choose Your Plan
          </h1>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">
            Start with a 14-day free trial. Add payment details to continue after trial.
          </p>
        </div>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-slate-800 rounded-lg p-1 flex gap-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'monthly' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'annual' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Annual <span className="text-emerald-400 text-xs ml-1">Save 15%</span>
            </button>
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Professional Plan */}
          <Card 
            className={`relative cursor-pointer transition-all duration-200 ${
              selectedPlan === 'professional' 
                ? 'border-blue-500 border-2 bg-slate-800/80' 
                : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
            }`}
            onClick={() => setSelectedPlan('professional')}
            data-testid="plan-professional"
          >
            {selectedPlan === 'professional' && (
              <div className="absolute -top-3 -right-3 bg-blue-500 rounded-full p-1">
                <Check className="h-5 w-5 text-white" />
              </div>
            )}
            <CardHeader>
              <div className="flex items-center gap-2">
                <Zap className="h-6 w-6 text-blue-400" />
                <CardTitle className="text-white text-xl">Professional</CardTitle>
              </div>
              <CardDescription className="text-slate-400">
                For growing law firms that need full case and billing management
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">
                  ${billingCycle === 'annual' ? '67.99' : '79.99'}
                </span>
                <span className="text-slate-400">/user/month</span>
                <p className="text-sm text-slate-500 mt-1">
                  {billingCycle === 'annual' ? 'Billed annually ($815.90/year)' : 'Billed monthly'}
                </p>
              </div>
              <ul className="space-y-3">
                {planFeatures.professional.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-slate-300">
                    <Check className="h-4 w-4 text-emerald-400 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Expert Plan */}
          <Card 
            className={`relative cursor-pointer transition-all duration-200 ${
              selectedPlan === 'expert' 
                ? 'border-purple-500 border-2 bg-slate-800/80' 
                : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
            }`}
            onClick={() => setSelectedPlan('expert')}
            data-testid="plan-expert"
          >
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 text-white">
              Most Popular
            </Badge>
            {selectedPlan === 'expert' && (
              <div className="absolute -top-3 -right-3 bg-purple-500 rounded-full p-1">
                <Check className="h-5 w-5 text-white" />
              </div>
            )}
            <CardHeader>
              <div className="flex items-center gap-2">
                <Crown className="h-6 w-6 text-purple-400" />
                <CardTitle className="text-white text-xl">Expert</CardTitle>
              </div>
              <CardDescription className="text-slate-400">
                For advanced firms ready to automate and operate with intelligence
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">
                  ${billingCycle === 'annual' ? '127.49' : '149.99'}
                </span>
                <span className="text-slate-400">/user/month</span>
                <p className="text-sm text-slate-500 mt-1">
                  {billingCycle === 'annual' ? 'Billed annually ($1,529.90/year)' : 'Billed monthly'}
                </p>
              </div>
              <ul className="space-y-3">
                {planFeatures.expert.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-slate-300">
                    <Check className="h-4 w-4 text-purple-400 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Start Trial Button */}
        <div className="text-center">
          <Button
            size="lg"
            className={`px-8 py-6 text-lg ${
              selectedPlan === 'expert' 
                ? 'bg-purple-600 hover:bg-purple-700' 
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
            onClick={handleStartTrial}
            disabled={!selectedPlan || starting}
            data-testid="start-trial-btn"
          >
            {starting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Redirecting to checkout...
              </>
            ) : (
              <>
                Continue to Payment Setup
                {selectedPlan && ` - ${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)}`}
              </>
            )}
          </Button>
          <p className="text-slate-500 text-sm mt-4">
            14-day free trial included. You won't be charged until your trial ends.
          </p>
        </div>

        {/* Enterprise CTA */}
        <div className="mt-12 text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Building className="h-5 w-5 text-amber-400" />
            <span className="text-white font-semibold">Enterprise</span>
          </div>
          <p className="text-slate-400 mb-4">
            Need custom pricing, dedicated support, or advanced integrations for your large firm?
          </p>
          <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
            Contact Sales
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SelectPlan;
